import React, { useEffect, useState } from 'react'
import { Link, graphql, navigate } from 'gatsby'
import styled, { css } from 'styled-components'
import { camelCase } from 'lodash'
import Img from 'gatsby-image/withIEPolyfill'
import { Helmet } from 'react-helmet'
import { useQueryParam, StringParam } from 'use-query-params'
import { useStore } from '../hooks/useStore'

import {
  Box,
  Button,
  Container,
  LoadingSpinner,
  P,
  H3,
  H1,
  H2,
  Paragraph,
  AlertGroup,
  Tile,
  IconContainer,
  ButtonUnstyled,
  ScreenReaderText,
} from '@allied-solutions/affinity'
import {
  Rocket02,
  ArrowRight,
  Synced,
  Upload01,
  DocumentSearch02,
} from '@allied-solutions/affinity-react-icons/dist/standard'

import { theme } from '../theme.js'
import { ArticleGrid, Layout, SiteLayout, Nav } from '../components'
import { useFetchBorrower, useCiamAuthorize } from '../hooks'

const StyledH1 = styled(H1)`
  mark {
    display: inline-block;
    line-height: 0em;
    padding-bottom: 0.5em;
    background-color: #93cf78;
    color: ${props => props.theme.colors.text.default};
  }
`

const LeftSide = styled(Box)`
  position: relative;
  text-align: center;
`

const StyledImg = styled(Img)`
  --hero-img-position: 90% 20%;
  border-bottom-left-radius: ${props => props.theme.space[18]};
  height: 200px;
  overflow: hidden;

  ${props => props.theme.mediaQueries.md} {
    height: 100%;
    width: 100%;
    --hero-img-position: -90% 20%;
    border-bottom-left-radius: ${props => props.theme.space[40]};
  }

  ${props => props.theme.mediaQueries.lg} {
    --hero-img-position: 85%;
    border-bottom-left-radius: ${props => props.theme.space[48]};
  }

  ${props => props.theme.mediaQueries.xl} {
    --hero-img-position: right center;
    border-bottom-left-radius: ${props => props.theme.space[48]};
  }
`

const FullWidthAlertWrapper = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 10000;
`

const StyledAlert = styled(AlertGroup.Alert)`
  padding: 30px;

  > div:first-of-type span:nth-of-type(2) {
    width: 100%;
    text-align: left;
  }
`

const StyledTile = styled(Tile)`
  border-radius: ${props => props.theme.radii.xl};
  padding: ${props => props.theme.space['2xl']};
  flex: 1 1 0%;
  min-width: 0;
  width: 100%;

  ${Tile.Header} {
    padding-bottom: ${props => props.theme.space['2xl']};
  }

  ${Tile.Footer} {
    padding-top: ${props => props.theme.space['2xl']};
    border-top: none;
  }
`

const ClickableStyledTile = styled(StyledTile)`
  position: relative;
  cursor: pointer;

  ${Button} {
    pointer-events: none;
  }
`

const StretchedLink = styled(Link)`
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: inherit;
  text-decoration: none;

  &:focus {
    outline: 2px solid ${props => props.theme.colors.blue};
    outline-offset: 2px;
  }
`

const StretchedButton = styled(ButtonUnstyled)`
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: inherit;
  cursor: pointer;

  &:focus {
    outline: 2px solid ${props => props.theme.colors.blue};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
  }
`

const AboutIllustration = styled.img`
  height: 300px;
  width: auto;
  max-width: none;
`

const ProcessStepBox = styled(Box)`
  box-shadow: 0 2px 4px -2px rgba(0, 112, 185, 0.06),
    0 4px 8px -2px rgba(0, 112, 185, 0.1);
`

const Home = ({
  data: {
    file: { childImageSharp },
    mdx: { frontmatter },
  },
}) => {
  const refId = useQueryParam('id', StringParam)[0] || ''

  const { state, dispatch } = useStore()

  const [isFetchingBorrower, setIsFetchingBorrower] = useState(false)

  const { getBorrower } = useFetchBorrower(
    '/enter-reference-id/confirm-identity/'
  )

  const { handleAuthorize, isAuthorizing } = useCiamAuthorize()

  const fetchBorrowerFromQueryString = async refId => {
    if (!refId) return

    setIsFetchingBorrower(true)
    await getBorrower(refId).catch(error => {
      console.error(error)
      navigate('/systems-down/')
    })

    setIsFetchingBorrower(false)
  }

  useEffect(() => {
    fetchBorrowerFromQueryString(refId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refId])

  return (
    <Layout>
      <Helmet>
        <title>{frontmatter.metaTitle}</title>
        <meta name="description" content={frontmatter.metaDescription} />
      </Helmet>
      <SiteLayout>
        {state.IsAccountDeleted && (
          <FullWidthAlertWrapper>
            <AlertGroup id="upload-status-alert-group" width="100%">
              <StyledAlert
                id="account-deleted-message"
                timeout={5000}
                type="info"
                isDismissable
                py={['6xl', 2]}
                onHidden={() => {
                  dispatch({
                    type: 'addToState',
                    payload: { IsAccountDeleted: false },
                  })
                }}
              >
                You successfully deleted your profile.
              </StyledAlert>
            </AlertGroup>
          </FullWidthAlertWrapper>
        )}
        {(state.TriggeredLogout || state.TriggeredTimeout) && (
          <FullWidthAlertWrapper>
            <AlertGroup id="upload-status-alert-group" width="100%">
              <StyledAlert
                id="logged-out-message"
                timeout={5000}
                type="info"
                isDismissable
                py={['6xl', 2]}
                onHidden={() => {
                  dispatch({
                    type: 'addToState',
                    payload: {
                      TriggeredLogout: false,
                      TriggeredTimeout: false,
                    },
                  })
                }}
              >
                {state.TriggeredTimeout
                  ? 'You have been logged out due to inactivity.'
                  : 'You have successfully signed out from your account.'}
              </StyledAlert>
            </AlertGroup>
          </FullWidthAlertWrapper>
        )}
        {state.TriggeredAccessDenied && (
          <FullWidthAlertWrapper>
            <AlertGroup id="upload-status-alert-group" width="100%">
              <StyledAlert
                id="access-denied-message"
                type="info"
                isDismissable
                py={['6xl', 2]}
                onHidden={() => {
                  dispatch({
                    type: 'addToState',
                    payload: {
                      TriggeredAccessDenied: false,
                    },
                  })
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexGrow={1}
                >
                  You must log in to access this page.
                  <Button
                    onClick={() => handleAuthorize()}
                    variant="white"
                    disabled={isAuthorizing}
                  >
                    {isAuthorizing ? (
                      <Box as="span" display="inline-flex" alignItems="center">
                        <LoadingSpinner size="sm" aria-hidden />
                        <ScreenReaderText>Logging in…</ScreenReaderText>
                      </Box>
                    ) : (
                      'Login'
                    )}
                  </Button>
                </Box>
              </StyledAlert>
            </AlertGroup>
          </FullWidthAlertWrapper>
        )}
        <Box
          height="85px"
          width="100%"
          position="absolute"
          top="0"
          left="0"
          right="0"
          zIndex="100"
          css={`
            ${props => props.theme.mediaQueries.lg} {
              background-color: white;
            }
            @media only screen and (min-width: 1380px) {
              background-color: transparent;
            }
          `}
        >
          <Nav underlineHome />
        </Box>

        {/* hero */}
        <Box>
          <Box
            bg="teal50"
            position="relative"
            overflow={['hidden', 'hidden', 'hidden', 'visible']}
          >
            <Container
              pt={[18, 18, 14, 36]}
              pb={[8, 8, 8, 17]}
              px="0"
              maxWidth="100%"
            >
              <Box display="flex" flexDirection={['column', null, null, 'row']}>
                <LeftSide
                  width={['100%', null, null, '53%']}
                  order={[0, 0, 0, 0]}
                  pl={[8, 8, 8, 20]}
                  pr={[8, 8, 8, 8]}
                >
                  <StyledH1
                    typeStyle={['displaySmall.black', 'displayMedium.black']}
                    textAlign={['center', 'center', 'center', 'left']}
                    dangerouslySetInnerHTML={{
                      __html: frontmatter.hero.headline,
                    }}
                    px={['0', '12', '12', '0']}
                    mt={['md', 'md', 'md', '0']}
                    mb="0"
                  />
                  {frontmatter.hero.blurb && (
                    <Paragraph
                      mt="2xl"
                      mb={['5xl', '5xl', '5xl', '7xl']}
                      typeStyle="bodyLarge"
                      textAlign={['center', 'center', 'center', 'left']}
                    >
                      {frontmatter.hero.blurb}
                    </Paragraph>
                  )}
                  <Box
                    display="flex"
                    justifyContent={[
                      'center',
                      'center',
                      'center',
                      'flex-start',
                    ]}
                    flexDirection={['column', 'row']}
                    gap={['2xl', '2xl', '2xl', '4xl']}
                  >
                    <ClickableStyledTile>
                      <ClickableStyledTile.Header>
                        <Box display="flex" alignItems="center" gap="lg">
                          <IconContainer icon={Rocket02} size="md" />
                          <P
                            id="first-time-users-title"
                            typeStyle="titleSmall.bold"
                            mb="0"
                          >
                            First-time users
                          </P>
                        </Box>
                      </ClickableStyledTile.Header>
                      <P
                        typeStyle="bodyMedium"
                        color="text.secondary"
                        textAlign="left"
                        mb="0"
                      >
                        Use the Reference ID from your mailer to submit
                        insurance for review.
                      </P>
                      <ClickableStyledTile.Footer>
                        <Box display="flex" justifyContent="flex-start">
                          <Button
                            ghost
                            icon={ArrowRight}
                            iconPos="right"
                            id="first-time-users-action"
                            size="lg"
                            variant="brand"
                            px="md"
                            tabIndex={-1}
                            text
                          >
                            Start verification
                          </Button>
                        </Box>
                      </ClickableStyledTile.Footer>
                      <StretchedLink
                        to="/enter-reference-id/"
                        aria-labelledby="first-time-users-title first-time-users-action"
                      />
                    </ClickableStyledTile>
                    <ClickableStyledTile>
                      <ClickableStyledTile.Header>
                        <Box display="flex" alignItems="center" gap="lg">
                          <IconContainer icon={Synced} size="md" />
                          <P
                            id="returning-users-title"
                            typeStyle="titleSmall.bold"
                            mb="0"
                          >
                            Returning users
                          </P>
                        </Box>
                      </ClickableStyledTile.Header>
                      <P
                        typeStyle="bodyMedium"
                        color="text.secondary"
                        textAlign="left"
                        mb="0"
                      >
                        Sign in to check the status of previously submitted
                        insurance.
                      </P>
                      <ClickableStyledTile.Footer>
                        <Box
                          display="flex"
                          justifyContent="flex-start"
                          alignItems="center"
                          gap="md"
                        >
                          <Button
                            ghost
                            icon={ArrowRight}
                            iconPos="right"
                            id="returning-users-action"
                            size="lg"
                            variant="brand"
                            px="md"
                            tabIndex={-1}
                            text
                          >
                            Check status
                          </Button>
                          {isAuthorizing ? <LoadingSpinner /> : ''}
                        </Box>
                      </ClickableStyledTile.Footer>
                      {state?.hasSessionToken ? (
                        <StretchedLink
                          to="/dashboard/"
                          aria-labelledby="returning-users-title returning-users-action"
                        />
                      ) : (
                        <StretchedButton
                          type="button"
                          disabled={isAuthorizing}
                          aria-labelledby="returning-users-title returning-users-action"
                          aria-busy={isAuthorizing}
                          onClick={() => handleAuthorize()}
                        />
                      )}
                    </ClickableStyledTile>
                  </Box>
                </LeftSide>
                <Box
                  width={['100%', '100%', '100%', 5 / 12]}
                  position={['relative', 'relative', 'relative', 'absolute']}
                  top={[null, null, null, 0]}
                  right={[null, null, null, 0]}
                  bottom={[null, null, null, 0]}
                  mt={[null, null, '2xl', 0]}
                  mb={['2xl', '2xl', null, 0]}
                  flexShrink={0}
                  order={[-1, -1, -1, 0]}
                  height={['200px', '400px', '400px', '100%']}
                >
                  <StyledImg
                    fluid={childImageSharp.fluid}
                    alt=""
                    loading="eager"
                    objectPosition="var(--hero-img-position)"
                    backgroundColor={theme.colors.teal400}
                  />
                </Box>
              </Box>
            </Container>
          </Box>
        </Box>

        {/* about */}
        <Container py={['7xl', '7xl', '7xl', '8xl']} mx="0" px="5xl">
          <Box position="relative">
            <Box
              display="flex"
              flexDirection={['column', 'column', 'column', 'row']}
              gap={['5xl', '5xl', '5xl', '10xl']}
              alignItems="center"
            >
              <Box
                width={['100%', '100%', '100%', '300px']}
                display="flex"
                justifyContent="center"
              >
                <AboutIllustration
                  src="/trusted-financial-illustration.svg"
                  alt="We work with trusted financial institutions."
                />
              </Box>
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  mb={['5xl', '5xl', '5xl', '6xl']}
                  flexDirection={['column', 'column', 'row']}
                  justifyContent={['center', 'center', 'center', 'flex-start']}
                >
                  <Box
                    borderRight={['none', 'none', '1px solid']}
                    borderBottom={['1px solid', '1px solid', 'none', 'none']}
                    borderBottomColor={[
                      'border.defaultLight',
                      'border.defaultLight',
                      null,
                      null,
                    ]}
                    borderRightColor={[null, null, 'border.defaultLight']}
                    pr={[0, 0, '5xl']}
                    py="xl"
                    mr={[0, 0, '5xl']}
                  >
                    <img src="/allied-logo.svg" alt="" />
                  </Box>
                  <Box pt="lg">
                    <img
                      src="your-banking-institution.svg"
                      alt="Your Banking Institution"
                    />
                  </Box>
                </Box>
                <H2
                  typeStyle={[
                    'headlineMedium.black',
                    'headlineMedium.black',
                    'displaySmall.black',
                    'displaySmall.black',
                  ]}
                  px={['xl', 'xl', 'xl', 0]}
                  textAlign={['center', 'center', 'center', 'left']}
                >
                  {frontmatter.about.headline}
                </H2>
                <P
                  typeStyle="bodyLarge"
                  textAlign={['center', 'center', 'center', 'left']}
                >
                  MyInsuranceInfo (MII) is a partnership between{' '}
                  <a
                    href="https://www.alliedsolutions.net/technology/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Allied Solutions - Technology"
                    style={{ textDecoration: 'none', fontWeight: 500 }}
                  >
                    Allied Solutions
                  </a>{' '}
                  and your financial institution to help simplify your
                  verification process. Learn more using the resources below.
                </P>
              </Box>
            </Box>
          </Box>
        </Container>
        <Box
          pt="1px"
          pb={[20, 12]}
          bg="teal50"
          display="flex"
          flexDirection={['column', 'row']}
        >
          <Container
            py={['7xl', '7xl', '7xl', '8xl']}
            px="5xl"
            display="flex"
            flexDirection="column"
          >
            <Box mb="5xl">
              <H2
                typeStyle={['headlineMedium.black', 'displaySmall.black']}
                textAlign={['center', 'center', 'center', 'left']}
              >
                Designed to keep you moving!
              </H2>
              <P
                typeStyle="bodyLarge"
                width={['100%', '100%', '100%', '57%']}
                textAlign={['center', 'center', 'center', 'left']}
              >
                Our streamlined process takes the hassle out of verification, so
                you can move on with your day. Here is what to expect:
              </P>
            </Box>
            <Box
              display={['contents', null, null, 'flex']}
              alignItems="flex-start"
              gap="11rem"
              flexDirection={['column', null, null, 'row']}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                order={[1, 1, 1, 0]}
                mb={[0, 0, '7xl', 0]}
                width={['100%', '100%', '65%', 'auto']}
                mx={[null, null, 'auto', '0']}
              >
                <ProcessStepBox
                  display="flex"
                  alignItems="center"
                  gap="lg"
                  bg="white"
                  py="2xl"
                  pl="2xl"
                  pr="7xl"
                  borderRadius="lg"
                >
                  <IconContainer icon={Upload01} size="md" />
                  <P typeStyle="labelLarge.medium" mb="0">
                    Submit your insurance documentation to MII
                  </P>
                </ProcessStepBox>
                <Box px="5xl">
                  <Box height="31px" width="2px" bg="navyBlue" />
                </Box>
                <ProcessStepBox
                  display="flex"
                  alignItems="center"
                  gap="lg"
                  bg="white"
                  py="2xl"
                  pl="2xl"
                  pr="7xl"
                  borderRadius="lg"
                >
                  <IconContainer icon={DocumentSearch02} size="md" />
                  <P typeStyle="labelLarge.medium" mb="0">
                    MII reviews documents for missing information
                  </P>
                </ProcessStepBox>
                <Box px="5xl">
                  <Box height="31px" width="2px" bg="navyBlue" />
                </Box>
                <ProcessStepBox
                  display="flex"
                  alignItems="center"
                  gap="lg"
                  bg="white"
                  py="2xl"
                  pl="2xl"
                  pr="7xl"
                  borderRadius="lg"
                >
                  <IconContainer icon={Synced} size="md" />
                  <P typeStyle="labelLarge.medium" mb="0">
                    Assist to verify with lender requirements
                  </P>
                </ProcessStepBox>
              </Box>
              <Box
                order={[-1, -1, -1, 0]}
                display="flex"
                justifyContent="center"
                mb={['5xl', '5xl', '5xl', 0]}
                width={['300px', '360px', '360px', '400px']}
                mx="auto"
              >
                <img
                  src="/keep-you-moving-illustration.svg"
                  alt="Designed to keep you moving!"
                />
              </Box>
            </Box>
          </Container>
        </Box>

        {/* support */}
        <Box
          borderTopRightRadius={80}
          pt="1px"
          pb={['7xl', '7xl', '8xl', 0]}
          boxShadow="0 -2px 4px -2px rgba(0, 112, 185, 0.06), 0 -4px 8px -2px rgba(0, 112, 185, 0.04)"
          bg="white"
          mt={-20}
        >
          <Container px={8} pt="8xl" pb="0">
            <Box mb="5xl">
              <H2
                typeStyle={['headlineLarge.black', 'displaySmall.black']}
                textAlign={['center', 'center', 'center', 'left']}
              >
                More resources.
              </H2>
              <P
                typeStyle="bodyLarge"
                width={['100%', '100%', '100%', '57%']}
                textAlign={['center', 'center', 'center', 'left']}
              >
                We provide content that serves as a guide for how you can get
                the most out of the MII experience. Check out these popular
                resources:
              </P>
            </Box>
            <ArticleGrid
              borderBottom="1px solid"
              borderBottomColor="border.defaultLight"
              pb={['xl', '5xl']}
              mb="5xl"
            />
            <Box display={[null, null, null, 'flex']}>
              <Box width={[null, null, null, 1 / 3]}>
                <H2
                  typeStyle="headlineMedium.black"
                  textAlign={['center', 'center', 'center', 'left']}
                >
                  {frontmatter.support.headline}
                </H2>
                {frontmatter.support.buttonText &&
                  frontmatter.support.buttonLink && (
                    <Box
                      display="flex"
                      justifyContent={[
                        'center',
                        'center',
                        'center',
                        'flex-start',
                      ]}
                    >
                      <Button
                        aria-label="See all FAQs"
                        id={`Home__SupportSection__Button--${camelCase(
                          frontmatter.support.buttonText
                        )}`}
                        forwardedAs={Link}
                        to={frontmatter.support.buttonLink}
                        rounded
                        boxShadow="blue"
                        size="lg"
                        mt={4}
                        mb={[10, 10, 10, 20]}
                        css={`
                          padding-left: ${props => props.theme.space[8]};
                          padding-right: ${props => props.theme.space[8]};
                        `}
                      >
                        <P
                          mb={0}
                          typeStyle="labelLarge"
                          textAlign="center"
                          color="white"
                        >
                          {frontmatter.support.buttonText}
                        </P>
                      </Button>
                    </Box>
                  )}
              </Box>
              <Box width={[null, null, null, 2 / 3]} mb={[0, 0, 0, '8xl']}>
                <Box mb="5xl">
                  <H3
                    typeStyle="titleSmall.bold"
                    textAlign={['center', 'center', 'center', 'left']}
                  >
                    Why did I get this letter in the mail?
                  </H3>
                  <P
                    typeStyle="bodyLarge"
                    textAlign={['center', 'center', 'center', 'left']}
                  >
                    You received this notice from your financial institution
                    because your loan agreement requires that you verify and
                    maintain a certain level of insurance coverage for your
                    collateral (such as a car, home, boat, or RV). If you want
                    to find out more,{' '}
                    <a href="/support/why-did-i-receive-a-letter-telling-me-to-verify-my-insurance/">
                      read this article.
                    </a>
                  </P>
                </Box>
                <Box mb="5xl">
                  <H3
                    typeStyle="titleSmall.bold"
                    textAlign={['center', 'center', 'center', 'left']}
                  >
                    How long does insurance verification take?
                  </H3>
                  <P
                    typeStyle="bodyLarge"
                    textAlign={['center', 'center', 'center', 'left']}
                  >
                    Our submission process usually takes 5 minutes. You will
                    receive an email confirmation after you submit your
                    insurance information. Then, we will review and confirm your
                    verification within 2-3 business days. Once reviewed, you
                    will receive an email confirming whether you are verified or
                    need to provide more information. You can also check your
                    status online by{' '}
                    <ButtonUnstyled
                      typeStyle="bodyLarge"
                      color="text.link"
                      css={css`
                        text-decoration: underline;
                        &:focus {
                          outline: none;
                        }

                        &:focus:not(:focus-visible) {
                          outline: none;
                        }

                        &:focus-visible {
                          outline: 3px solid
                            ${props => props.theme.colors.blue600};
                          z-index: 11;
                          outline-offset: 3px;
                          border-radius: 5px;
                        }
                      `}
                      onClick={() => handleAuthorize()}
                    >
                      creating an account.
                    </ButtonUnstyled>{' '}
                    {isAuthorizing ? (
                      <Box as="span" display="inline-flex" alignItems="center">
                        <LoadingSpinner size="sm" aria-hidden />
                        <ScreenReaderText>Logging in…</ScreenReaderText>
                      </Box>
                    ) : (
                      ''
                    )}
                  </P>
                </Box>
                <Box>
                  <H3
                    typeStyle="titleSmall.bold"
                    textAlign={['center', 'center', 'center', 'left']}
                  >
                    How do I know if I have the right coverage?
                  </H3>
                  <P
                    typeStyle="bodyLarge"
                    textAlign={['center', 'center', 'center', 'left']}
                  >
                    You can find the exact coverage requirements on your loan
                    agreement documents. If you don’t have a copy of your loan
                    agreement, you can always call the lender that gave you the
                    loan. They should be able to tell you how much coverage you
                    need.
                  </P>
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>
      </SiteLayout>
      {isFetchingBorrower && (
        <Box
          position="fixed"
          top={0}
          right={0}
          bottom={0}
          left={0}
          zIndex={5000}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="rgba(255,255,255,0.9)"
        >
          <LoadingSpinner mr={4} /> One moment...
        </Box>
      )}
    </Layout>
  )
}

const pageQuery = graphql`
  query {
    file(relativePath: { eq: "home-hero.webp" }) {
      childImageSharp {
        fluid(maxWidth: 1200) {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    mdx(frontmatter: { slug: { eq: "home" } }) {
      frontmatter {
        metaTitle
        metaDescription
        hero {
          headline
          blurb
          buttonText
          buttonLink
          buttonCheckStatusText
          buttonCheckStatusLink
        }
        about {
          headline
        }
        support {
          headline
          buttonText
          buttonLink
        }
      }
    }
  }
`

export default Home
export { pageQuery }
